import { useState } from 'react'
import { GradientCircle } from '../GradientCircle'
import { gradientVariants, type GradientVariant } from '../GradientOverlay/gradientVariants'
import { cn } from '#utils'
import { stripPlaceholderName } from '@butter/library/shared'

/**
 * Avatar size presets mapped to Tailwind classes
 */
const sizeClasses = {
  xs: 'h-6 w-6',
  sm: 'h-8 w-8',
  md: 'h-10 w-10',
  lg: 'h-12 w-12',
  xl: 'h-14 w-14',
  '2xl': 'h-16 w-16',
} as const

/**
 * Text size classes for initials based on avatar size
 */
const textSizeClasses = {
  xs: 'text-[10px]',
  sm: 'text-xs',
  md: 'text-sm',
  lg: 'text-base',
  xl: 'text-lg',
  '2xl': 'text-2xl font-semibold leading-7',
} as const

type AvatarSize = keyof typeof sizeClasses

export interface AvatarProps {
  /** Name to generate initials from and use as alt text */
  name: string
  /** Gradient color variant (used when no image provided) */
  variant?: GradientVariant
  /** Size preset (xs, sm, md, lg, xl) */
  size?: AvatarSize
  /** Optional className for custom sizing (overrides size preset) */
  className?: string
  /** Optional icon to display instead of initials (when no image) */
  icon?: React.ReactNode
  /** Optional image URL to display instead of gradient/initials */
  src?: string
  /** Optional alt text for image (defaults to name) */
  alt?: string
  /** Whether to hide from screen readers (decorative) */
  ariaHidden?: boolean
  /**
   * When true, renders a variant-colored outline (the variant's gradient `color2` at 80% alpha) around the avatar.
   * Applies to both image and gradient/initials render branches.
   * Defaults to `false`.
   */
  outlined?: boolean
}

/**
 * Valid gradient variants for validation
 */
const VALID_VARIANTS: GradientVariant[] = [
  'beige', 'gold', 'diamond', 'platinum',
  'purple', 'aqua', 'blue', 'green', 'red'
]

/**
 * Default variant when none provided or invalid
 */
const DEFAULT_VARIANT: GradientVariant = 'purple'

/**
 * Extract initials from a name
 *
 * @example
 * getInitials("Jane Smith") // "JS"
 * getInitials("Madonna") // "M"
 * getInitials("Jean-Pierre Dupont") // "JD"
 */
function getInitials(rawName: string): string {
  // Imported accounts with no name on file carry `[unknown]` in both halves;
  // without this they render "[[" — strip it and fall through to the empty circle.
  const name = stripPlaceholderName(rawName)
  if (name.length === 0) {
    return ''
  }

  const parts = name.split(/\s+/)

  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase()
  }

  // First and last name initials
  const firstInitial = parts[0].charAt(0).toUpperCase()
  const lastInitial = parts[parts.length - 1].charAt(0).toUpperCase()

  return `${firstInitial}${lastInitial}`
}

/**
 * Validate and normalize variant value
 */
function normalizeVariant(variant: string | undefined | null): GradientVariant {
  if (!variant) {
    return DEFAULT_VARIANT
  }

  if (VALID_VARIANTS.includes(variant as GradientVariant)) {
    return variant as GradientVariant
  }

  return DEFAULT_VARIANT
}

/**
 * Avatar component
 *
 * Displays a gradient circle with user initials, an icon, or a profile image.
 * Wraps GradientCircle with convenient props for common use cases.
 *
 * @example
 * ```tsx
 * // With initials
 * <Avatar name="Jane Smith" variant="purple" size="md" />
 *
 * // With icon
 * <Avatar name="Team Alpha" icon={<GroupIcon className="size-5 text-white" />} />
 *
 * // With image (replaces gradient/initials)
 * <Avatar name="Jane Smith" src="/avatar.jpg" size="lg" />
 *
 * // Custom size
 * <Avatar name="User" className="h-20 w-20" />
 * ```
 */
export function Avatar({
  name,
  variant,
  size = 'md',
  className,
  icon,
  src,
  alt,
  ariaHidden = true,
  outlined = false,
}: AvatarProps) {
  const [imageError, setImageError] = useState(false)
  const initials = getInitials(name)
  const validVariant = normalizeVariant(variant)
  const sizeClass = className || sizeClasses[size]
  const textSizeClass = textSizeClasses[size]
  const outlineBorderColor = outlined
    ? `${gradientVariants[validVariant].color2}CC`
    : undefined

  // Show image if src provided and hasn't errored
  if (src && !imageError) {
    return (
      <div
        className={cn(
          'rounded-full overflow-hidden flex-shrink-0',
          sizeClass,
          outlined && 'border',
        )}
        style={outlined ? { borderColor: outlineBorderColor } : undefined}
      >
        <img
          src={src}
          alt={alt || stripPlaceholderName(name)}
          onError={() => setImageError(true)}
          className="w-full h-full object-cover"
        />
      </div>
    )
  }

  // Fallback to gradient circle with initials/icon
  const circle = (
    <GradientCircle
      variant={validVariant}
      text={icon ? undefined : initials}
      textClassName={textSizeClass}
      className={sizeClass}
      aria-hidden={ariaHidden}
    >
      {icon}
    </GradientCircle>
  )

  if (outlined) {
    return (
      <div
        className="rounded-full border shrink-0 inline-flex"
        style={{ borderColor: outlineBorderColor }}
      >
        {circle}
      </div>
    )
  }

  return circle
}
